<script>
import STable from '@/components/Table/index'
import {
  loadAccidentList,
  createAccident,
  updateAccident,
  deleteAccident,
  exportAccidentList,
  loadPermissionList
} from '@/api/data'
import { getI18nLanguage } from '@/locales'
import moment from 'moment'

const TYPE_COLORS = ['', 'blue', 'orange', 'red']

export default {
  name: 'Accident',
  components: {
    STable
  },
  data () {
    return {
      locale: {
        emptyText: this.$t('table.emptyText')
      },
      col: {
        md: 12,
        lg: 8,
        xl: 6,
        xxl: 4
      },
      typeOptions: [
        { value: 1, label: this.$t('accident.type.1') },
        { value: 2, label: this.$t('accident.type.2') },
        { value: 3, label: this.$t('accident.type.3') }
      ],
      queryParam: {
        name: '',
        accident_type: undefined,
        qTime: []
      },
      drivers: [],
      // 表头
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          align: 'center',
          width: 70
        },
        {
          title: this.$t('accident.field.name'),
          dataIndex: 'name',
          align: 'center',
          width: 130
        },
        {
          title: this.$t('accident.field.type'),
          dataIndex: 'accident_type',
          align: 'center',
          width: 120,
          scopedSlots: { customRender: 'type' }
        },
        {
          title: this.$t('accident.field.time'),
          dataIndex: 'accident_time',
          align: 'center',
          width: 160
        },
        {
          title: this.$t('accident.field.location'),
          dataIndex: 'accident_location',
          width: 180,
          ellipsis: true
        },
        {
          title: this.$t('accident.field.summary'),
          dataIndex: 'accident_summary',
          width: 200,
          ellipsis: true
        },
        {
          title: this.$t('accident.field.reason'),
          dataIndex: 'accident_reason',
          width: 200,
          ellipsis: true
        },
        {
          title: this.$t('accident.field.countermeasure'),
          dataIndex: 'countermeasure',
          width: 200,
          ellipsis: true
        },
        {
          title: this.$t('accident.field.action'),
          dataIndex: 'action',
          align: 'center',
          width: 120,
          fixed: 'right',
          scopedSlots: { customRender: 'action' }
        }
      ],
      // 加载数据方法 必须为 Promise 对象
      loadData: parameter => {
        const params = {
          pageNo: parameter.pageNo,
          pageSize: parameter.pageSize,
          name: this.queryParam.name,
          accident_type: this.queryParam.accident_type
        }
        if (this.queryParam.qTime && this.queryParam.qTime.length === 2) {
          params.start_time = this.queryParam.qTime[0].format('YYYY-MM-DD') + ' 00:00:00'
          params.end_time = this.queryParam.qTime[1].format('YYYY-MM-DD') + ' 23:59:59'
        }
        return loadAccidentList(params).then(res => {
          const result = res.result
          for (const item of result.data) {
            item.type_name = this.$t('accident.type.' + item.accident_type)
          }
          return result
        })
      },
      modalVisible: false,
      modalLoading: false,
      editingRecord: null,
      form: this.$form.createForm(this)
    }
  },
  created () {
    this.loadDrivers()
  },
  methods: {
    getErrorMessage (msg) {
      if (typeof msg === 'string') {
        return msg
      }
      const lang = getI18nLanguage()
      const langKey = lang.replace('-', '').toLowerCase()
      if (langKey.includes('zh')) {
        return msg['zh'] || msg['zh-CN'] || msg['en'] || msg['en-US'] || ''
      } else if (langKey.includes('ja')) {
        return msg['ja'] || msg['ja-JP'] || msg['en'] || msg['en-US'] || ''
      } else {
        return msg['en'] || msg['en-US'] || msg['zh'] || ''
      }
    },
    loadDrivers () {
      loadPermissionList({
        type: 'all',
        pageNo: 1,
        pageSize: 1000
      }).then(res => {
        const data = (res.result && res.result.data) || []
        this.drivers = data.map(u => {
          const name = u.name || ''
          const account = u.account || ''
          return {
            value: u.user_id,
            label: account ? `${name}(${account})` : name
          }
        })
      })
    },
    typeColor (type) {
      return TYPE_COLORS[type] || 'blue'
    },
    handleSearch () {
      this.$refs.table.refresh(true)
    },
    handleReset () {
      this.queryParam.name = ''
      this.queryParam.accident_type = undefined
      this.queryParam.qTime = []
      this.$refs.table.refresh(true)
    },
    openCreate () {
      this.editingRecord = null
      this.modalVisible = true
      this.$nextTick(() => {
        this.form.resetFields()
      })
    },
    openEdit (record) {
      this.editingRecord = record
      this.modalVisible = true
      this.$nextTick(() => {
        this.form.resetFields()
        this.form.setFieldsValue({
          user_id: record.user_id,
          accident_type: record.accident_type,
          accident_time: moment(record.accident_time),
          accident_location: record.accident_location,
          accident_summary: record.accident_summary,
          accident_reason: record.accident_reason,
          countermeasure: record.countermeasure
        })
      })
    },
    handleSave () {
      const { form: { validateFields } } = this
      validateFields((err, values) => {
        if (err) {
          return
        }
        this.modalLoading = true
        const params = {
          user_id: values.user_id,
          accident_type: values.accident_type,
          accident_time: values.accident_time.format('YYYY-MM-DD HH:mm:ss'),
          accident_location: values.accident_location || '',
          accident_summary: values.accident_summary || '',
          accident_reason: values.accident_reason || '',
          countermeasure: values.countermeasure || ''
        }
        const req = this.editingRecord
          ? updateAccident(Object.assign({ id: this.editingRecord.id }, params))
          : createAccident(params)
        req.then(res => {
          if (res.code === 0) {
            this.$message.success(this.$t('accident.message.save.success'))
            this.modalVisible = false
            this.$refs.table.refresh(this.editingRecord ? false : true)
          } else {
            this.$message.error(this.getErrorMessage(res.msg) || this.$t('accident.message.save.fail'))
          }
        }).catch(() => {
          this.$message.error(this.$t('accident.message.save.fail'))
        }).finally(() => {
          this.modalLoading = false
        })
      })
    },
    handleDelete (record) {
      const self = this
      this.$confirm({
        title: self.$t('accident.delete.confirm.title'),
        content: self.$t('accident.delete.confirm.content'),
        okText: self.$t('accident.action.delete'),
        cancelText: self.$t('accident.action.cancel'),
        onOk () {
          return deleteAccident({ id: record.id }).then(res => {
            if (res.code === 0) {
              self.$message.success(self.$t('accident.message.delete.success'))
              self.$refs.table.refresh(false)
            } else {
              self.$message.error(self.getErrorMessage(res.msg) || self.$t('accident.message.delete.fail'))
            }
          }).catch(() => {
            self.$message.error(self.$t('accident.message.delete.fail'))
          })
        }
      })
    },
    handleExport () {
      const params = {
        name: this.queryParam.name,
        accident_type: this.queryParam.accident_type,
        lang: getI18nLanguage(),
        fileName: this.$t('accident.export.filename') + '.xlsx'
      }
      if (this.queryParam.qTime && this.queryParam.qTime.length === 2) {
        params.start_time = this.queryParam.qTime[0].format('YYYY-MM-DD') + ' 00:00:00'
        params.end_time = this.queryParam.qTime[1].format('YYYY-MM-DD') + ' 23:59:59'
      }
      const hide = this.$message.loading(this.$t('accident.export.loading'), 0)
      exportAccidentList(params).then(res => {
        this.saveResultAsExcel(res, params.fileName)
      }).catch(() => {
        this.$message.error(this.$t('accident.export.fail'))
      }).finally(() => {
        hide()
      })
    }
  }
}
</script>

<template>
  <div class="accident-page">
    <div class="filter-card">
      <div class="filter-card__body">
        <a-row :gutter="16">
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <label class="filter-item__label">{{ $t('accident.field.name') }}</label>
              <a-input class="filter-item__input" v-model="queryParam.name" allowClear @pressEnter="handleSearch" />
            </div>
          </a-col>
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <label class="filter-item__label">{{ $t('accident.field.type') }}</label>
              <a-select class="filter-item__input" v-model="queryParam.accident_type" allowClear>
                <a-select-option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <label class="filter-item__label">{{ $t('accident.field.time') }}</label>
              <a-range-picker class="filter-item__input" v-model="queryParam.qTime" />
            </div>
          </a-col>
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item filter-item--actions">
              <a-button type="primary" @click="handleSearch">{{ $t('accident.action.search') }}</a-button>
              <a-button @click="handleReset">{{ $t('accident.action.reset') }}</a-button>
            </div>
          </a-col>
        </a-row>
      </div>
    </div>

    <div class="table-card">
      <div class="table-card__toolbar">
        <a-button type="primary" icon="plus" @click="openCreate">{{ $t('accident.action.create') }}</a-button>
        <a-button icon="download" @click="handleExport">{{ $t('accident.action.export') }}</a-button>
      </div>
      <s-table
        ref="table"
        :columns="columns"
        :data="loadData"
        :locale="locale"
        :scroll="{ x: 1400 }"
        size="small">
        <span slot="type" slot-scope="text, record">
          <a-tag :color="typeColor(record.accident_type)">{{ record.type_name }}</a-tag>
        </span>
        <div slot="action" slot-scope="text, record" class="action-cell">
          <a-button type="link" @click="openEdit(record)">{{ $t('accident.action.edit') }}</a-button>
          <span class="action-divider"></span>
          <a-button type="link" class="action-link--danger" @click="handleDelete(record)">{{ $t('accident.action.delete') }}</a-button>
        </div>
      </s-table>
    </div>

    <!-- 添加/编辑事故 -->
    <a-modal
      v-model="modalVisible"
      :title="editingRecord ? $t('accident.modal.edit.title') : $t('accident.modal.create.title')"
      :confirm-loading="modalLoading"
      :mask-closable="false"
      :width="640"
      @ok="handleSave">
      <a-form :form="form" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-item :label="$t('accident.field.driver')">
          <a-select
            v-decorator="['user_id', { rules: [{ required: true, message: $t('accident.form.user_id.tip') }] }]"
            show-search
            option-filter-prop="children"
            :placeholder="$t('accident.form.user_id.placeholder')">
            <a-select-option v-for="d in drivers" :key="d.value" :value="d.value">{{ d.label }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('accident.field.type')">
          <a-select
            v-decorator="['accident_type', { rules: [{ required: true, message: $t('accident.form.type.tip') }] }]"
            :placeholder="$t('accident.form.type.placeholder')">
            <a-select-option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('accident.field.time')">
          <a-date-picker
            v-decorator="['accident_time', { rules: [{ required: true, message: $t('accident.form.time.tip') }] }]"
            show-time
            style="width: 100%"
            :placeholder="$t('accident.form.time.placeholder')" />
        </a-form-item>
        <a-form-item :label="$t('accident.field.location')">
          <a-input
            v-decorator="['accident_location']"
            :placeholder="$t('accident.form.location.placeholder')"
            allow-clear />
        </a-form-item>
        <a-form-item :label="$t('accident.field.summary')">
          <a-textarea
            v-decorator="['accident_summary']"
            :placeholder="$t('accident.form.summary.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-item>
        <a-form-item :label="$t('accident.field.reason')">
          <a-textarea
            v-decorator="['accident_reason']"
            :placeholder="$t('accident.form.reason.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-item>
        <a-form-item :label="$t('accident.field.countermeasure')">
          <a-textarea
            v-decorator="['countermeasure']"
            :placeholder="$t('accident.form.countermeasure.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<style scoped lang="less">
// 筛选条 + 数据卡的骨架（配色、圆角、投影、标签堆叠、工具条、操作列、卡片节奏）
// 十个列表页共用一份，见 assets/list-page.less
@import '../../assets/list-page.less';
</style>
